import React, { useState, useEffect } from "react";

import { addDoc, collection } from "firebase/firestore";

import FlashcardEditorDialog from "./flashcards/FlashcardEditorDialog";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  Alert,
  ListItemText,
  Box,
  Button,
  CircularProgress,
  Typography,
  Grid,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { lighten, alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import { useSnackbar } from "./SnackbarHandler";
import { query, where, getDocs, limit } from "firebase/firestore";
import { Menu, MenuItem } from "@mui/material";
import { db } from "./firebase";
import getListStyles from "./listStyles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { SubjectsList, GradeLevels } from "./BankFilters";
import ListAltIcon from "@mui/icons-material/ListAlt";

import BankFilters from "./BankFilters";

// Subject Icons
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CalculateIcon from "@mui/icons-material/Calculate";
import TranslateIcon from "@mui/icons-material/Translate";
import PublicIcon from "@mui/icons-material/Public";
import PsychologyIcon from "@mui/icons-material/Psychology";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import BrushIcon from "@mui/icons-material/Brush";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import BusinessIcon from "@mui/icons-material/Business";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import ChurchIcon from "@mui/icons-material/Church";
import ScienceIcon from "@mui/icons-material/Science";
import WaveIcon from "@mui/icons-material/Waves";
import BiotechIcon from "@mui/icons-material/Biotech";
import ForestIcon from "@mui/icons-material/Forest";

const FlashcardsBank = ({ open, onClose, user, styles, theme }) => {
  const navigate = useNavigate();
  const [flashcards, setFlashcards] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);

  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const [menuPosition, setMenuPosition] = useState(null);
  const [menuFlashcard, setMenuFlashcard] = useState(null);

  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);

  const { listItemStyles, listItemIconStyles, listItemTextStyles } =
    getListStyles(theme, theme.palette.mode === "dark");

  const adminIds = [
    "Z1dF5PGfjvdBGS9egzACvcSRZiN2",
    "x2rjH7JxWxc6GnFgPSTxGxN1bm63",
    "Z6BLSMsm38Un5mgW0O6Z3TToHq12",
  ];

  useEffect(() => {
    const fetchFlashcards = async () => {
      setIsLoading(true);
      try {
        const flashcardsQuery = query(
          collection(db, "flashcardCollections"),
          where("userId", "in", adminIds),
          where("tags", "!=", null),
          limit(50),
        );

        const querySnapshot = await getDocs(flashcardsQuery);
        const fetchedFlashcards = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((flashcard) => flashcard.tags?.length > 0);

        setFlashcards(fetchedFlashcards);
      } catch (error) {
        console.error("Error fetching flashcards:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchFlashcards();
    }
  }, [open]);

  const handleFlashcardCardClick = (event, flashcard) => {
    setMenuPosition({
      top: event.clientY + 2, // Add 2px offset
      left: event.clientX + 2, // Add 2px offset
    });
    setMenuFlashcard(flashcard);
  };

  // Add this close handler
  const handleMenuClose = () => {
    setMenuPosition(null);
    setMenuFlashcard(null);
  };

  const filteredFlashcards = flashcards
    .filter((flashcard) => {
      let matchesSubject = true;
      let matchesGrade = true;

      if (selectedSubjects.length > 0) {
        matchesSubject = selectedSubjects.some((subject) =>
          flashcard.tags?.includes(subject),
        );
      }
      if (selectedGrades.length > 0) {
        matchesGrade = selectedGrades.some((grade) =>
          flashcard.tags?.includes(grade),
        );
      }
      return matchesSubject && matchesGrade;
    })
    .sort((a, b) =>
      selectedSubject ? 0 : (b.upvotes || 0) - (a.upvotes || 0),
    );

  const handleFlashcardClick = (flashcard) => {
    const formattedName = flashcard.collectionName
      .toLowerCase()
      .replace(/\s+/g, "-");
    navigate(`/app/flashcards/${flashcard.id}/${formattedName}`);
    onClose();
  };

  const handleViewFlashcards = (collection) => {
    setSelectedCollection(collection);
    setIsEditorOpen(true);
  };

  const handleSaveToMyFlashcards = async (flashcards, collectionName, tags) => {
    try {
      const newCollection = {
        userId: user?.uid,
        collectionName: collectionName || "Unnamed Collection",
        flashcards: flashcards.map(
          ({ front, back, imageUrlFront, imageUrlBack }) => ({
            front,
            back,
            imageUrlFront,
            correct: null,
            imageUrlBack,
          }),
        ), // Tar bort correct-fältet

        tags: Array.isArray(tags) ? tags : [],
        createdAt: Date.now(),
        shared: false,
        isPublic: false,
        upvotes: 0,
        downvotes: 0,
        lastTrainingSession: null,
        userVotes: {},
        category: selectedCollection?.category || "",
        language: selectedCollection?.language || "",
        difficulty: selectedCollection?.difficulty || "",
        aiInstructions: selectedCollection?.aiInstructions || "",
        selectedModel: selectedCollection?.selectedModel || "",
      };

      await addDoc(collection(db, "flashcardCollections"), newCollection);
      showSnackbar("Flashcards har sparats i 'Mina flashcards'!", "success");
      setIsEditorOpen(false);
    } catch (error) {
      console.error("Error saving flashcards:", error);
      showSnackbar("Fel vid sparande av flashcards: " + error.message, "error");
    }
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            borderRadius: "25px",
            position: "relative",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#192734" : "#fff",
          },
        }}
      >
        <Alert
          severity="error"
          sx={{
            width: { xs: "100%", md: "237px" },
            margin: "0 auto",
            //  background: "#a4c4a42e",
            border: "1px solid #d7454426",
            //background: "none",
            padding: "4px",
            borderRadius: 4,
            position: "absolute",
            bottom: 20,
            left: 20,

            // left: "50%",
            // transform: "translateX(-50%)",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
              fontWeight: "600",
              color: "#d84746",
            },
            "& .MuiAlert-icon": {
              display: "flex",
              alignItems: "center",
              marginRight: 1,
              marginTop: 0,
              marginLeft: 1,
              fontSize: "1.1rem",
            },
          }}
        >
          <span>Fler flashcards är på väg!</span>
        </Alert>
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "700", fontSize: "1.5rem" }}
            >
              Flashcard-banken
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Left Sidebar - Subject Filter */}
            <Grid item xs={12} md={3}>
              <SubjectsList
                selectedSubjects={selectedSubjects} // Changed prop
                setSelectedSubjects={setSelectedSubjects} // Changed prop
                setIsLoading={setIsLoading}
                theme={theme}
              />
            </Grid>

            {/* Right Content - Flashcards */}
            <Grid item xs={12} md={9}>
              {/* Grade Level Filters */}
              <GradeLevels
                selectedGrades={selectedGrades} // Changed prop
                setSelectedGrades={setSelectedGrades} // Changed prop
                setIsLoading={setIsLoading}
                styles={styles}
                theme={theme}
                selectedSubject={selectedSubject}
                selectedSubjects={selectedSubjects}
                filteredItems={filteredFlashcards} // or filteredFlashcards
                bankType="flashcards" // or "flashcards"
              />

              {/* Flashcards Grid */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {isLoading ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <CircularProgress size={50} />
                    <Typography sx={{ mt: 2, color: "text.secondary" }}>
                      Laddar flashcards...
                    </Typography>
                  </Box>
                ) : filteredFlashcards.length === 0 ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <img
                      src="/img/empty.png"
                      alt="Empty"
                      style={{ width: "200px", marginBottom: "24px" }}
                    />
                    <Typography
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        fontWeight: "500",
                        fontSize: "1.3rem",
                        width: { xs: "100%", md: "70%" },
                      }}
                    >
                      Här var det tomt! Vi jobbar på att fylla på
                      Flashcard-banken. Titta in snart igen! :)
                    </Typography>
                    <Button
                      variant="print"
                      onClick={() => {
                        setSelectedSubjects([]);
                        setSelectedGrades([]);
                      }}
                      sx={{ mt: 4 }}
                    >
                      Rensa filter
                    </Button>
                  </Box>
                ) : (
                  filteredFlashcards.map((flashcard) => (
                    <Grid item xs={12} sm={6} md={4} key={flashcard.id}>
                      <Box
                        onClick={(e) => handleFlashcardCardClick(e, flashcard)}
                        sx={{
                          ...styles.freshBox,
                          borderRadius: "18px",
                          padding: "0px",
                          height: "230px",

                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          border:
                            theme.palette.mode === "dark"
                              ? undefined
                              : "1px solid #f3f0f0",
                          "&:hover": {
                            border:
                              theme.palette.mode === "dark"
                                ? undefined
                                : `1px solid ${theme.palette.primary.main}`,
                            transform: "translateY(-4px)",
                            "& .button-container": {
                              opacity: 1,
                            },
                            // "& > div:nth-child(3)": {
                            //   "& > div:first-child": {
                            //     filter: "blur(3px)",
                            //   },
                            // },
                            // "& > div:nth-child(3) > div:last-child": {
                            //   // This targets just the bottom container
                            //   filter: "blur(3px)",
                            // },
                          },
                        }}
                      >
                        <IconButton
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 9999999,
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            //backgroundImage:
                            //  "url(https://sportkuriren.se/wp-content/uploads/np.jpg)",
                            background:
                              theme.palette.mode === "dark"
                                ? "#384b63"
                                : "rgb(250 250 250)",
                            boxShadow:
                              "rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        />
                        <Box
                          sx={{
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            padding: "20px",
                            mt: 3,
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: "37px", // Adjusts for the bottom stats container
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              component="div"
                              sx={{
                                fontSize: "1.2rem",
                                fontWeight: "600",
                                textAlign: "center",
                                p: 2,
                                position: "relative",
                              }}
                            >
                              {flashcard.collectionName}
                            </Typography>
                          </Box>
                          {/* Bottom container with stats */}
                          <Box
                            sx={{
                              mt: "auto",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            {/* Likes/upvotes */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <ThumbUpOffAltIcon
                                sx={{
                                  fontSize: "17px",
                                  color: "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "14px",
                                  lineHeight: 1,
                                  fontWeight: "600",
                                  color: "text.secondary",
                                }}
                              >
                                {flashcard.upvotes || 0}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Menu
        open={Boolean(menuPosition)}
        onClose={() => setMenuPosition(null)}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            ...styles.roundMenu,
            ...styles.roundMenuLarger,
          },
        }}
      >
        <MenuItem
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
          onClick={() => {
            handleViewFlashcards(menuFlashcard);
            handleMenuClose();
            setMenuPosition(null);
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <ListAltIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Visa flashcards"
            secondary="Eller spara i ditt arkiv"
          />
        </MenuItem>
        <MenuItem
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
          onClick={() => {
            handleFlashcardClick(menuFlashcard);
            handleMenuClose();
            setMenuPosition(null);
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <PlayArrowIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Öva på flashcards"
            secondary="Spela interaktivt"
          />
        </MenuItem>
      </Menu>

      <FlashcardEditorDialog
        open={isEditorOpen}
        onClose={() => {
          setIsEditorOpen(false);
          setSelectedCollection(null); // Lägg till denna rad
        }}
        collection={selectedCollection}
        user={user}
        showSnackbar={showSnackbar}
        onEditComplete={(flashcards, collectionName, tags) => {
          if (selectedCollection?.userId === user.uid) {
            // Handle normal edit completion if user owns the collection
          } else {
            handleSaveToMyFlashcards(
              flashcards,
              collectionName, // Använd värdet som kommer från dialogen
              tags, // Använd värdet som kommer från dialogen
            );
          }
        }}
        saveButtonText={
          selectedCollection?.userId === user.uid
            ? "Spara ändringar"
            : "Spara i 'Mina flashcards'"
        }
      />
    </>
  );
};

export default FlashcardsBank;
